import { Check, ArrowUpRight } from "lucide-react";
import useDocTitle from "../lib/useDocTitle";
import ComplianceDisclaimer from "../components/ComplianceDisclaimer";

function Tier({ name, price, cadence, bestFor, features, recommended, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative flex flex-col rounded-xl border bg-white p-6 ${
        recommended ? "border-[#2563EB] ring-2 ring-[#2563EB]/15 shadow-sm" : "border-slate-200"
      }`}
    >
      {recommended && (
        <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-[#2563EB] px-2.5 py-1 text-[11px] font-semibold text-white">
          Recommended
        </span>
      )}
      <h3 className="text-base font-semibold text-[#0A2540]">{name}</h3>
      <p className="mt-4">
        <span className="text-3xl font-semibold text-[#0A2540]">{price}</span>
        <span className="ml-1.5 text-sm text-slate-500">{cadence}</span>
      </p>
      <p className="mt-2 text-sm text-slate-600">{bestFor}</p>
      <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={16} className="mt-0.5 text-[#2563EB]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Pricing() {
  useDocTitle(
    "Pricing · Lulo Software",
    "Transparent pricing for Lulo Software products. Appeal Architect licenses from $49. PrimePillar from $19/month. BlendSafe free safety checks with optional Founding Member access.",
    "Lulo Software pricing, Appeal Architect pricing, BlendSafe pricing, PrimePillar pricing, B2B SaaS pricing",
    "/pricing",
  );

  return (
    <div data-testid="pricing-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20 max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Pricing</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight text-[#0A2540]">Pricing</h1>
          <p className="mt-4 text-base text-slate-600 leading-7">
            Each product in the Lulo Software portfolio has its own pricing structure based on
            functionality and customer needs.
          </p>
        </div>
      </section>

      {/* Appeal Architect */}
      <section data-testid="pricing-appeal" className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#0A2540]">Appeal Architect</h2>
              <p className="mt-2 text-sm text-slate-600">
                Available now.{" "}
                <a
                  href="https://appealarchitect.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2563EB] hover:text-[#1d4ed8] font-medium"
                  data-testid="pricing-appeal-live-link"
                >
                  View live pricing at appealarchitect.com →
                </a>
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Tier
              testId="pricing-tier-scanner"
              name="Documentation Scanner"
              price="From $49"
              cadence="/ one-time"
              bestFor="Operational teams reviewing a single platform"
              features={[
                "Single-platform documentation review",
                "Structured documentation workflow",
                "1 generated reference document",
                "Email support",
              ]}
            />
            <Tier
              testId="pricing-tier-guided"
              name="Guided Plan"
              price="From $89"
              cadence="/ one-time"
              bestFor="Operational teams needing comprehensive documentation"
              recommended
              features={[
                "Everything in Documentation Scanner",
                "Multi-platform documentation review",
                "3 reference document variations",
                "Evidence organization",
                "Email support",
              ]}
            />
            <Tier
              testId="pricing-tier-architect"
              name="Architect Plan"
              price="From $199"
              cadence="/ one-time"
              bestFor="Complex multi-platform documentation needs"
              features={[
                "Everything in Guided Plan",
                "Documentation Readiness Review",
                "Priority email support",
                "Multi-channel documentation guidance",
              ]}
            />
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-[#F9FAFB] p-5">
              <p className="text-sm font-semibold text-[#0A2540]">Documentation Monitoring Subscription</p>
              <p className="mt-1 text-sm text-slate-600">
                <span className="text-xl font-semibold text-[#0A2540]">$29/month</span>
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-[#F9FAFB] p-5">
              <p className="text-sm font-semibold text-[#0A2540]">Setup Readiness Audit</p>
              <p className="mt-1 text-sm text-slate-600">
                <span className="text-xl font-semibold text-[#0A2540]">$149</span>
                <span className="ml-1 text-sm text-slate-500">one-time</span>
              </p>
            </div>
          </div>

          <a
            href="https://appealarchitect.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-appeal-full-cta"
            className="mt-8 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
          >
            View full pricing details at appealarchitect.com <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      {/* BlendSafe */}
      <section data-testid="pricing-medskin" className="border-b border-slate-200 bg-[#F9FAFB]">
        <div className="lulo-container py-16 lg:py-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#0A2540]">BlendSafe</h2>
              <p className="mt-2 text-sm text-slate-600">
                Available now.{" "}
                <a
                  href="https://www.blendsafe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2563EB] hover:text-[#1d4ed8] font-medium"
                  data-testid="pricing-medskin-live-link"
                >
                  View live pricing at blendsafe.com →
                </a>
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Tier
              testId="pricing-tier-blendsafe-free"
              name="Free"
              price="Free"
              cadence=""
              bestFor="Anyone running quick skincare-medication checks"
              features={[
                "Unlimited basic safety checks",
                "SAFE / CAUTION / AVOID verdict per check",
                "Search by product name or ingredient list",
                "Barcode and photo ingredient scan",
                "Category browser across 10 dermatology contexts",
                "No account required",
              ]}
            />
            <Tier
              testId="pricing-tier-blendsafe-founding"
              name="Founding Member"
              price="Early access"
              cadence="· first 100 users"
              bestFor="Early adopters who want memory, monitoring, and personalisation"
              recommended
              features={[
                "Saved AM/PM routines and history",
                "Personalised skin logic (sensitive, rosacea, pregnancy-safe)",
                "Continuous routine monitoring between checks",
                "Dermatologist-ready PDF export",
                "Extended scanning and advanced analysis",
                "Permanent Founding Member badge",
              ]}
            />
          </div>

          <p className="mt-6 text-xs text-slate-500 leading-6">
            Founding Member access is offered as a limited early-access programme
            of 100 spots. Pricing for premium features post-launch is announced
            on blendsafe.com.
          </p>

          <a
            href="https://www.blendsafe.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-medskin-full-cta"
            className="mt-8 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
          >
            View full pricing details at blendsafe.com <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      {/* PrimePillar */}
      <section data-testid="pricing-ptp" className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#0A2540]">PrimePillar</h2>
              <p className="mt-2 text-sm text-slate-600">
                Available now.{" "}
                <a
                  href="https://primepillar.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2563EB] hover:text-[#1d4ed8] font-medium"
                  data-testid="pricing-ptp-live-link"
                >
                  View live pricing at primepillar.io →
                </a>
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Tier
              testId="pricing-tier-ptp-scout"
              name="Scout"
              price="Free"
              cadence=""
              bestFor="Founders evaluating the platform"
              features={[
                "Top 5 public signals per week",
                "Basic Pain-to-Profit score breakdown",
                "1 deep-dive brief per month",
              ]}
            />
            <Tier
              testId="pricing-tier-ptp-founder"
              name="Founder"
              price="$19"
              cadence="/ month"
              bestFor="Indie founders shipping micro-SaaS"
              recommended
              features={[
                "Full signal feed (50+ active opportunities)",
                "Unlimited deep-dive briefs",
                "Validate-an-Idea AI scorer",
                "Watchlist and PDF exports",
              ]}
            />
            <Tier
              testId="pricing-tier-ptp-pro"
              name="Pro"
              price="$49"
              cadence="/ month"
              bestFor="Small teams and serial builders"
              features={[
                "Everything in Founder",
                "Trending Pain Board access",
                "Priority scan refresh",
                "Email digest summaries",
              ]}
            />
          </div>

          <a
            href="https://primepillar.io"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-ptp-full-cta"
            className="mt-8 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
          >
            View full pricing details at primepillar.io <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      {/* Enterprise */}
      <section data-testid="pricing-enterprise" className="bg-white">
        <div className="lulo-container py-16 lg:py-20 max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Enterprise &amp; Volume Discounts</h2>
          <p className="mt-3 text-sm text-slate-700 leading-7">
            Volume discounts and enterprise plans are available for qualifying business
            customers across all our products. For inquiries, contact{" "}
            <a
              href="mailto:partners@lulosoftware.com"
              className="text-[#2563EB] hover:text-[#1d4ed8] font-medium"
              data-testid="pricing-enterprise-email"
            >
              partners@lulosoftware.com
            </a>
            .
          </p>
        </div>
      </section>

      <ComplianceDisclaimer />
    </div>
  );
}
